<template>
  <aside class="game-illustration" ref="ctx">
    <span class="illustration__bg" ref="bg"/>
    <div class="illustration__container" ref="illustration">
      <img
        class="illustration__image"
        data-phase="0"
        src="/img/illustrations/phase-0.jpg"
        alt="Illustration gucci off the grid"
      >
      <img
        class="illustration__image"
        data-phase="1"
        src="/img/illustrations/phase-1.jpg"
        alt="Illustration gucci off the grid"
      >
      <img
        class="illustration__image"
        data-phase="2"
        src="/img/illustrations/phase-2.jpg"
        alt="Illustration gucci off the grid"
      >
      <img
        class="illustration__image"
        data-phase="3"
        src="/img/illustrations/phase-3.jpg"
        alt="Illustration gucci off the grid"
      >
      <video
        class="illustration__image"
        data-phase="4"
        src="/img/illustrations/phase-4.mp4"
        type="video/mp4"
        preload="auto"
        crossorigin="anonymous"
        playsinline
        autoload
        muted
        loop
      />
    </div>

    <GameFeedback :mood="mood" />

    <div class="illustration__mobile-feedback" ref="mobileFeedback">
      <span v-html="mood === 'happy' ? 'GREAT!' : 'OHH NOOO!'" />
    </div>
  </aside>
</template>

<script>
import { mapState } from 'vuex';
import anime from '@/assets/libs/anime';
import GameFeedback from '@/components/GameFeedback.vue';
import { inApp } from '@/scripts/utils';
import { animations, initials } from '@/scripts/animations';

export default {
  name: 'GameIllustration',

  components: {
    GameFeedback,
  },

  data() {
    return {
      origins: [
        { x: 55, y: 25 },
        { x: 45, y: 0 },
        { x: 50, y: 35 },
        { x: 50, y: 30 },
      ],
      imageSizes: {
        x: 2500,
        y: 2500,
        currentX: 0,
        currentY: 0,
      },
      illustrationShown: false,
      inApp: inApp(),
    };
  },

  computed: {
    ...mapState({
      game: 'currentGame',
    }),
    mood() {
      let mood = 'none';
      if (this.$store.state.currentGame.answerChecked) {
        mood = this.$store.state.currentGame.answerCorrect ? 'happy' : 'sad';
      }
      return mood;
    },
  },

  mounted() {
    this.init();

    this.showIllustrationHandler = (delay = 0, res = false) => this.showIllustration(delay, res);
    this.$eventHub.$on('showIllustration', this.showIllustrationHandler);
    this.$eventHub.$on('restartIllustration', this.restart);
    this.$eventHub.$on('illustrationUpdateResult', this.updateResult);
    this.$nextTick(() => this.$eventHub.$on('windowResize', this.resize));

    this.pictures = [...this.$el.querySelectorAll('.illustration__image')];
    this.setupPictures();
  },

  methods: {
    init() {
      anime.set([this.$refs.ctx, this.$refs.illustration], {
        webkitClipPath: 'inset(100% -0.02% -0.03% -0.04%)',
        clipPath: 'inset(100% -0.02% -0.03% -0.04%)',
      });
    },
    showIllustration(delay) {
      this.illustrationShown = true;

      this.$eventHub.$emit('logoChanges', 'show', delay);
      if (!this.inApp && window.innerWidth >= 768) this.$eventHub.$emit('showCollectionLink', true, delay);

      if (window.innerWidth < 768 && !this.game.gameEnded) {
        const feedback = this.$el.querySelector('.game-feedback');
        initials.get('feedback')(feedback);
        animations.get('feedback')(feedback, this.$store.state.currentGame.answerCorrect);

        anime({
          targets: this.$refs.mobileFeedback,
          translateY: [20, 0],
          opacity: [0, 1],
          easing: 'cubicBezier(.5,0,0,1)',
          duration: 1000,
        });
      }

      const tl = anime.timeline({
        easing: 'cubicBezier(.5,0,0,1)',
        duration: inApp ? 1000 : 1800,
        delay,
        complete: () => {
          if (window.innerWidth < 768 && !this.game.gameEnded) {
            if (this.game.answerCorrect) this.updateResult();
            else this.hideIllustration(800);
          }
        },
      });

      tl
        .add({
          targets: [this.$refs.ctx, this.$refs.illustration],
          webkitClipPath: ['inset(100% -0.02% -0.03% -0.04%)', 'inset(0.01% -0.02% -0.03% -0.04%)'],
          clipPath: ['inset(100% -0.02% -0.03% -0.04%)', 'inset(0.01% -0.02% -0.03% -0.04%)'],
          delay: anime.stagger(250, { start: delay }),
        }, 0);

      if (window.innerWidth < 768 && this.game.gameEnded) {
        tl
          .add({
            targets: this.$refs.bg,
            scaleY: 0.77,
            transformOrigin: ['50% 0 0', '50% 0 0'],
          }, 800);
      }

      return tl;
    },
    hideIllustration(delay = 0) {
      this.$eventHub.$emit('hideCollectionLink');
      this.$eventHub.$emit('logoChanges', 'hide');
      this.illustrationShown = false;

      if (window.innerWidth < 768 && !this.game.gameEnded) {
        anime({
          targets: this.$refs.mobileFeedback,
          translateY: [0, 20],
          opacity: [1, 0],
          easing: 'cubicBezier(.5,0,0,1)',
          duration: inApp ? 500 : 1000,
          delay: 600,
        });
      }

      const tl = anime.timeline({
        easing: 'cubicBezier(.5,0,0,1)',
        duration: inApp ? 1000 : 1800,
        complete: () => this.$eventHub.$emit('illustrationHidden'),
      });

      tl
        .add({
          targets: [this.$refs.ctx, this.$refs.illustration],
          webkitClipPath: ['inset(0.01% -0.02% -0.03% -0.04%)', 'inset(100% -0.02% -0.03% -0.04%)'],
          clipPath: ['inset(0.01% -0.02% -0.03% -0.04%)', 'inset(100% -0.02% -0.03% -0.04%)'],
          delay: anime.stagger(250, { start: delay, from: 'last' }),
        }, 0);

      return tl;
    },
    updateResult() {
      const oldIndex = this.game.partialScore - 1;
      const elIn = this.$el.querySelector(`[data-phase="${this.game.partialScore}"]`);
      const elOut = this.$el.querySelector(`[data-phase="${oldIndex}"]`);
      const tl = anime.timeline({
        duration: inApp ? 1000 : 1800,
        easing: 'cubicBezier(.5,0,0,1)',
        complete: () => {
          if (window.innerWidth < 768) this.hideIllustration();
        },
      });

      if (this.game.partialScore === 4) elIn.play();

      tl
        .add({
          targets: elOut,
          transformOrigin: ['50% 50% 0', `${this.origins[oldIndex].x}% ${this.origins[oldIndex].y}% 0`],
          scale: [
            { value: oldIndex >= 3 ? 1.25 : 1.5, duration: inApp ? 1000 : 1800 },
            { value: 1, duration: inApp ? 1000 : 1800, delay: 600 },
          ],
        }, 0)
        .add({
          targets: elIn,
          transformOrigin: ['50% 50% 0', `${this.origins[oldIndex].x}% ${this.origins[oldIndex].y}% 0`],
          scale: [
            { value: oldIndex >= 3 ? 1.25 : 1.5, duration: inApp ? 1000 : 1800 },
            { value: 1, duration: inApp ? 1000 : 1800, delay: 600 },
          ],
          opacity: { value: 1, delay: inApp ? 800 : 1400, duration: inApp ? 600 : 800 },
        }, 0);

      return tl;
    },
    restart() {
      this.game.gameEnded = false;
      if (!this.inApp) this.$eventHub.$emit('showCollectionLink', false);
      let tl;

      if (window.innerWidth < 768) {
        tl = anime.timeline({
          easing: 'cubicBezier(.5,0,0,1)',
          duration: inApp ? 1000 : 1800,
          complete: () => {
            this.setupPictures();
            this.$eventHub.$emit('illustrationHidden');
            this.$eventHub.$emit('logoChanges', 'hide');
            anime.set(this.$refs.bg, { scaleY: 1, transformOrigin: ['50% 0 0', '50% 0 0'] });
          },
        });

        tl
          .add({
            targets: [this.$refs.ctx, this.$refs.illustration],
            webkitClipPath: 'inset(0.01% -0.02% 100% -0.04%)',
            clipPath: 'inset(0.01% -0.02% 100% -0.04%)',
            delay: anime.stagger(250),
          }, 0);
      } else {
        let reset = false;
        this.$eventHub.$emit('logoChanges', 'hide');

        tl = anime.timeline({
          easing: 'cubicBezier(.5,0,0,1)',
          duration: 1800,
          update: () => {
            if (tl.progress > 50 && !reset) {
              reset = true;
              this.$eventHub.$emit('showCollectionLink', true, 700);
              this.$eventHub.$emit('logoChanges', 'show');
              this.setupPictures();
            }
          },
        });

        tl
          .add({
            targets: [this.$refs.ctx, this.$refs.illustration],
            webkitClipPath: [
              { value: 'inset(0.01% -0.02% 100% -0.04%)' },
              { value: 'inset(100% -0.02% -0.03% -0.04%)', delay: 800, duration: 0.1 },
              {
                value: window.innerWidth < 768 ? 'inset(-0.01% -0.02% 100% -0.04%)' : 'inset(-0.01% -0.02% -0.03% -0.04%)',
                delay: 1200,
              },
            ],
            clipPath: [
              { value: 'inset(0.01% -0.02% 100% -0.04%)' },
              { value: 'inset(100% -0.02% -0.03% -0.04%)', delay: 800, duration: 0.1 },
              { value: 'inset(-0.01% -0.02% -0.03% -0.04%)', delay: 950 },
            ],
            delay: anime.stagger(250),
          }, 0);
      }


      return tl;
    },
    resize() {
      if (this.game.questionsIndex === null) return;

      if (this.game.questionsIndex >= 0) {
        const valHidden = window.innerWidth < 768 && !this.game.gameEnded ? 100 : -0.01;
        anime.set([this.$refs.ctx, this.$refs.illustration], {
          webkitClipPath: `inset(${valHidden}% -0.02% -0.03% -0.04%)`,
          clipPath: `inset(${valHidden}% -0.02% -0.03% -0.04%)`,
        });
      }

      if (this.game.gameEnded && window.innerWidth < 768) {
        anime.set(this.$refs.bg, { scaleY: 0.77, transformOrigin: '50% 0 0' });
      } else {
        anime.set(this.$refs.bg, { scaleY: 1, transformOrigin: '50% 0 0' });
      }

      if (this.illustrationShown && window.innerWidth < 768) {
        anime.set(this.$refs.mobileFeedback, { opacity: 0, translateY: 20 });
      } else if (!this.illustrationShown && window.innerWidth < 768) {
        anime.set(this.$refs.mobileFeedback, { opacity: 1, translateY: 0 });
      }
    },
    setupPictures() {
      this.pictures.forEach((picture, i) => anime.set(picture, { opacity: i > 0 ? 0 : 1 }));
    },
  },

  beforeDestroy() {
    this.$eventHub.$off('showIllustration', this.showIllustrationHandler);
    this.$eventHub.$off('restartIllustration', this.restart);
    this.$eventHub.$off('windowResize', this.resize);
  },
};
</script>

<style lang="scss" scoped>
.game-illustration {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  padding: calc(var(--unit-vh) * 0.8) calc(var(--unit-vw) * 0.35) calc(var(--unit-vh) * 1.2);

  pointer-events: none;

  @include mq(ipadP) {
    width: calc(var(--unit-vw) * 6);
    height: 100%;
    padding: calc(var(--unit-vh) * 0.8) calc(var(--unit-vw) * 0.8) var(--unit-vh);

    opacity: 1;
    pointer-events: unset;
  }

  @include mq($until: ipadP) {
    z-index: 1;
  }

  .illustration__bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    background-color: $color-green;
  }

  .illustration__container,
  .illustration__image {
    display: block;
    width: 100%;
    height: 100%;
  }

  .illustration__container {
    position: relative;
  }

  .illustration__image {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    object-fit: cover;
  }

  .illustration__mobile-feedback {
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: calc(var(--unit-vh) * 1.2);

    display: flex;
    align-items: center;
    justify-content: center;

    @extend %font-size-button;
    font-weight: 700;
    color: $color-bg;

    @include mq(ipadP) {
      display: none;
    }
  }
}
</style>
